import { useEffect, useRef, useState } from 'react'
import { renderAsync } from 'docx-preview'

interface DocxPreviewProps {
  blob: Blob | null
  className?: string
}

export default function DocxPreview({ blob, className = '' }: DocxPreviewProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [rendering, setRendering] = useState(false)
  const [rendered, setRendered] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!blob || !containerRef.current) return

    const render = async () => {
      setRendering(true)
      setRendered(false)
      setError(null)

      try {
        // Clear previous content
        if (containerRef.current) {
          containerRef.current.innerHTML = ''
        }

        await renderAsync(blob, containerRef.current!, undefined, {
          className: 'docx-content',
          inWrapper: true,
          ignoreWidth: true,
          ignoreHeight: true,
          renderHeaders: true,
          renderFooters: true,
          renderFootnotes: true,
          renderEndnotes: true,
        })
        setRendered(true)
      } catch (err) {
        console.error('Failed to render document:', err)
        setError('Failed to render document. The file may be corrupted or in an unsupported format.')
      } finally {
        setRendering(false)
      }
    }

    render()
  }, [blob])

  if (!blob) {
    return (
      <div className={`flex items-center justify-center p-12 ${className}`}>
        <div className="text-center">
          <i className="fa-solid fa-file-word text-4xl mb-4" style={{ color: 'rgba(148, 163, 184, 0.4)' }}></i>
          <p style={{ color: 'rgba(148, 163, 184, 0.6)' }}>No document to preview</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={`flex items-center justify-center p-12 ${className}`}>
        <div className="text-center">
          <i className="fa-solid fa-exclamation-triangle text-4xl mb-4" style={{ color: '#f87171' }}></i>
          <p style={{ color: 'rgba(248, 113, 113, 0.9)' }}>{error}</p>
        </div>
      </div>
    )
  }

  return (
    <div className={`docx-preview-container ${className}`} style={{ position: 'relative' }}>
      <style>{`
        .docx-preview-container {
          background: rgba(30, 41, 59, 0.5);
          border-radius: 12px;
          padding: 20px;
          overflow: auto;
        }
        .docx-preview-container .docx-wrapper {
          background: white !important;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
          margin: 0 auto;
          width: 100% !important;
        }
        .docx-preview-container .docx-wrapper > section.docx {
          padding: 40px 60px;
          min-height: 400px;
          width: 100% !important;
          box-sizing: border-box;
        }
        .docx-preview-container article {
          overflow: visible !important;
        }
      `}</style>
      {/* Loading overlay — container stays in DOM so renderAsync can target it */}
      {rendering && (
        <div style={{
          position: 'absolute', inset: 0, zIndex: 10,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(30, 41, 59, 0.8)', borderRadius: '12px',
        }}>
          <div className="text-center">
            <i className="fa-solid fa-spinner fa-spin text-4xl mb-4" style={{ color: '#60a5fa' }}></i>
            <p style={{ color: 'rgba(148, 163, 184, 0.8)' }}>Rendering document...</p>
          </div>
        </div>
      )}
      {/* Container is always mounted — never removed from DOM */}
      <div ref={containerRef} style={{ minHeight: rendering || !rendered ? '200px' : undefined }} />
    </div>
  )
}
